import { format, parseISO } from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone, getTimezoneOffset as getTZOffset } from 'date-fns-tz';

/**
 * Convert a date/time from one timezone to another using date-fns-tz (DST-aware)
 * @param date - The date to convert
 * @param fromTimezone - Source timezone (e.g., 'UTC', 'America/New_York')
 * @param toTimezone - Target timezone
 * @returns The converted date
 */
export function convertTimezone(date: Date | string, fromTimezone: string, toTimezone: string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (fromTimezone === toTimezone) {
    return dateObj;
  }

  const utcDate = fromZonedTime(dateObj, fromTimezone);
  return toZonedTime(utcDate, toTimezone);
}

/**
 * Format a date in a specific timezone
 * @param date - The date to format
 * @param timezone - The timezone to use
 * @param formatStr - The format string (default: 'h:mm a')
 * @returns Formatted date string
 */
export function formatInTimezoneCustom(
  date: Date | string,
  timezone: string,
  formatStr: string = 'h:mm a'
): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  try {
    return formatInTimeZone(dateObj, timezone, formatStr);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return format(dateObj, formatStr);
  }
}

/**
 * Get the current time in a specific timezone
 * @param timezone - The timezone
 * @returns Current date in the specified timezone
 */
export function getCurrentTimeInTimezone(timezone: string): Date {
  return toZonedTime(new Date(), timezone);
}

/**
 * Calculate the offset between two timezones in minutes (DST-aware)
 * @param timezone1 - First timezone
 * @param timezone2 - Second timezone
 * @param referenceDate - Date to use for DST calculation (defaults to now)
 * @returns Offset in minutes
 */
export function getTimezoneOffset(timezone1: string, timezone2: string, referenceDate: Date = new Date()): number {
  const offset1 = getTZOffset(timezone1, referenceDate);
  const offset2 = getTZOffset(timezone2, referenceDate);
  return (offset2 - offset1) / (1000 * 60);
}

/**
 * Format timezone offset for display (e.g., "EDT", "PST"), DST-aware
 * @param timezone - The timezone to format
 * @returns Formatted offset string
 */
export function formatTimezoneOffset(timezone: string): string {
  try {
    return formatInTimeZone(new Date(), timezone, 'zzz');
  } catch {
    return 'UTC';
  }
}

/**
 * Convert booking times to a different timezone
 * @param booking - The booking object
 * @param fromTimezone - Source timezone
 * @param toTimezone - Target timezone
 * @returns Booking with converted times
 */
export function convertBookingTimezone<T extends { startTime: string; endTime: string }>(
  booking: T,
  fromTimezone: string,
  toTimezone: string
): T {
  if (fromTimezone === toTimezone) {
    return booking;
  }

  const startDate = parseISO(booking.startTime);
  const endDate = parseISO(booking.endTime);

  const convertedStart = convertTimezone(startDate, fromTimezone, toTimezone);
  const convertedEnd = convertTimezone(endDate, fromTimezone, toTimezone);

  return {
    ...booking,
    startTime: convertedStart.toISOString(),
    endTime: convertedEnd.toISOString(),
  };
}

/**
 * Get timezone display name
 * @param timezone - The timezone value
 * @returns Display name with abbreviation
 */
export function getTimezoneDisplayName(timezone: string): string {
  try {
    const abbr = formatInTimeZone(new Date(), timezone, 'zzz');
    return `${timezone} (${abbr})`;
  } catch {
    return timezone;
  }
}

/**
 * Check if daylight saving time is active in a timezone (DST-aware)
 * @param timezone - The timezone to check
 * @param date - The date to check (defaults to now)
 * @returns True if DST is active
 */
export function isDSTActive(timezone: string, date: Date = new Date()): boolean {
  try {
    const jan = new Date(date.getFullYear(), 0, 1);
    const jul = new Date(date.getFullYear(), 6, 1);
    const janOffset = getTZOffset(timezone, jan);
    const julOffset = getTZOffset(timezone, jul);
    if (janOffset === julOffset) return false;
    const currentOffset = getTZOffset(timezone, date);
    return currentOffset === Math.max(janOffset, julOffset);
  } catch {
    return false;
  }
}

/**
 * Adjust time slots for timezone display
 * @param hour - Hour (0-23)
 * @param minute - Minute (0-59)
 * @param fromTimezone - Source timezone
 * @param toTimezone - Target timezone
 * @returns Adjusted hour and minute
 */
export function adjustTimeSlotForTimezone(
  hour: number,
  minute: number,
  fromTimezone: string,
  toTimezone: string
): { hour: number; minute: number } {
  if (fromTimezone === toTimezone) {
    return { hour, minute };
  }

  const date = new Date();
  date.setHours(hour, minute, 0, 0);

  const converted = convertTimezone(date, fromTimezone, toTimezone);

  return {
    hour: converted.getHours(),
    minute: converted.getMinutes(),
  };
}
